"use client"

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react"
import Link from "next/link"

import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Divider,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@coval/ui"
import { ChevronRight, Star } from "lucide-react"

const HOVER_OPEN_DELAY_MS = 100
const HOVER_CLOSE_DELAY_MS = 150

const isHovered = (element: HTMLElement | null) => {
  try {
    return element?.matches(":hover") ?? false
  } catch {
    return false
  }
}

export interface DashboardNavDashboard {
  id: string
  display_name: string
  is_default: boolean
  is_favorite: boolean
}

interface DashboardNavItem {
  key: string
  href: string
  base_href: string
  icon?: React.ElementType
  title: string
}

interface DashboardNavPopoverProps {
  item: DashboardNavItem
  dashboards: DashboardNavDashboard[]
  isLoading: boolean
  fetchError?: Error | string
  getHref: (path: string) => string
  onToggleFavorite: (dashboardId: string) => void
  isActive: (href: string, baseHref?: string) => boolean
  currentDashboardId?: string
  onCreateNewDashboard?: (displayName: string) => void
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function DashboardNavPopover({
  item,
  dashboards,
  isLoading,
  fetchError,
  getHref,
  onToggleFavorite,
  isActive,
  currentDashboardId: _currentDashboardId,
  onCreateNewDashboard,
  isOpen,
  onOpenChange,
}: DashboardNavPopoverProps) {
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const sortedDashboards = useMemo(() => {
    const starred = dashboards.filter((d) => d.is_favorite && !d.is_default)
    const regular = dashboards.filter((d) => !d.is_favorite && !d.is_default)
    const defaultDashboard = dashboards.find((d) => d.is_default)
    starred.sort((a, b) => a.display_name.localeCompare(b.display_name))
    regular.sort((a, b) => a.display_name.localeCompare(b.display_name))
    const result = []
    if (defaultDashboard) {
      result.push(defaultDashboard)
    }
    result.push(...starred)
    result.push(...regular)
    return result
  }, [dashboards])

  const scheduleOpen = useCallback(() => {
    if (openTimeoutRef.current) {
      return
    }
    openTimeoutRef.current = setTimeout(() => {
      onOpenChange(true)
      openTimeoutRef.current = null
    }, HOVER_OPEN_DELAY_MS)
  }, [onOpenChange])

  const cancelOpen = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
  }, [])

  const scheduleClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    closeTimeoutRef.current = setTimeout(() => {
      const active = document.activeElement
      const focusIsWithinPopover =
        contentRef.current?.contains(active) ||
        triggerRef.current?.contains(active)

      if (
        focusIsWithinPopover ||
        isHovered(triggerRef.current) ||
        isHovered(contentRef.current)
      ) {
        closeTimeoutRef.current = null
        return
      }
      onOpenChange(false)
      closeTimeoutRef.current = null
    }, HOVER_CLOSE_DELAY_MS)
  }, [onOpenChange])

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current)
        openTimeoutRef.current = null
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
        closeTimeoutRef.current = null
      }
    }
  }, [])

  const hasDashboardsToShow = sortedDashboards.length > 0
  const createDashboardHref = getHref("/dashboard")

  const createNameInputId = useId()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [createName, setCreateName] = useState("")

  const openCreateDialog = useCallback(() => {
    setCreateName("")
    setIsCreateOpen(true)
    onOpenChange(false)
  }, [onOpenChange])

  const handleCreateConfirm = useCallback(() => {
    const trimmedName = createName.trim()
    if (!trimmedName || !onCreateNewDashboard) {
      return
    }
    onCreateNewDashboard(trimmedName)
    setIsCreateOpen(false)
    setCreateName("")
  }, [createName, onCreateNewDashboard])

  return (
    <>
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <div
            ref={triggerRef}
            role="button"
            tabIndex={0}
            aria-label={`${item.title} dashboards`}
            aria-haspopup="menu"
            onMouseEnter={() => {
              cancelClose()
              scheduleOpen()
            }}
            onMouseLeave={() => {
              cancelOpen()
              scheduleClose()
            }}
            onBlur={scheduleClose}
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") {
                return
              }
              e.preventDefault()
              if (isOpen) {
                cancelOpen()
                scheduleClose()
              } else {
                cancelClose()
                onOpenChange(true)
              }
            }}
            onClick={(e) => e.preventDefault()}
            className={cn(
              "group flex items-center gap-2 px-2 py-1 rounded-sm cursor-default focus:outline-none",
              isActive(item.href, item.base_href) || isOpen
                ? "bg-surface-1"
                : "hover:bg-surface-1",
            )}
          >
            {item.icon &&
              React.createElement(item.icon, {
                className: "w-4 h-4 text-current shrink-0",
              })}
            <span className="text-sm font-medium truncate">{item.title}</span>
            <ChevronRight className="w-3 h-3 text-coval-text-muted shrink-0 ml-auto" />
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={0}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="w-max min-w-[180px] max-w-[320px] max-h-[80vh] flex flex-col overflow-hidden p-1"
        >
          <div
            ref={contentRef}
            className="flex flex-1 flex-col min-h-0 w-full"
            onFocus={cancelClose}
            onBlur={scheduleClose}
          >
            {fetchError ? (
              <p className="px-2 py-3 text-sm text-coval-danger-text">
                Failed to load dashboards
              </p>
            ) : isLoading ? (
              <p className="px-2 py-3 text-sm text-coval-text-muted">
                Loading…
              </p>
            ) : (
              <>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {hasDashboardsToShow &&
                    sortedDashboards.map((dashboard, index) => {
                      const href = dashboard.is_default
                        ? getHref("/dashboard")
                        : getHref(`/dashboard/${dashboard.id}`)
                      const displayName = dashboard.is_default
                        ? "Main"
                        : dashboard.display_name
                      const isHighlighted = index === 0
                      return (
                        <div
                          key={dashboard.id}
                          className={cn(
                            "group/row relative flex w-full cursor-pointer select-none items-center rounded-sm text-sm text-coval-text",
                            isHighlighted
                              ? "bg-surface-default-hover"
                              : "hover:bg-surface-default-hover",
                          )}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              onToggleFavorite(dashboard.id)
                            }}
                            className="shrink-0 p-1 ml-1 text-coval-text-muted"
                            aria-label={
                              dashboard.is_favorite
                                ? "Remove from favorites"
                                : "Add to favorites"
                            }
                          >
                            <Star
                              className={cn(
                                "w-3.5 h-3.5",
                                dashboard.is_favorite && "fill-current",
                              )}
                            />
                          </button>
                          <Link
                            href={href}
                            onClick={() => onOpenChange(false)}
                            className="flex-1 min-w-0 py-1.5 pl-1 pr-2 truncate font-medium"
                            aria-label={displayName}
                          >
                            {displayName}
                          </Link>
                        </div>
                      )
                    })}
                </div>
                {hasDashboardsToShow && <Divider className="my-1" />}
                {onCreateNewDashboard ? (
                  <div
                    className="relative flex w-full cursor-pointer select-none items-center rounded-sm text-sm text-coval-text hover:bg-surface-default-hover"
                    onClick={openCreateDialog}
                  >
                    <span className="flex-1 min-w-0 py-1.5 pl-2 pr-2 truncate font-medium">
                      New Dashboard
                    </span>
                  </div>
                ) : (
                  <div className="relative flex w-full cursor-pointer select-none items-center rounded-sm text-sm text-coval-text hover:bg-surface-default-hover">
                    <Link
                      href={createDashboardHref}
                      onClick={() => onOpenChange(false)}
                      aria-label="Go to Dashboards"
                      className="flex-1 min-w-0 py-1.5 pl-2 pr-2 truncate font-medium"
                    >
                      Go to Dashboards
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Dialog
        open={isCreateOpen}
        onOpenChange={(open) => {
          setIsCreateOpen(open)
          if (!open) {
            setCreateName("")
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create dashboard</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={createNameInputId}>Dashboard name</Label>
              <Input
                id={createNameInputId}
                aria-label="Dashboard name"
                placeholder="New Dashboard"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                maxLength={64}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleCreateConfirm()
                  }
                }}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleCreateConfirm}
              disabled={!createName.trim()}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
